import React, { useContext, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useTrimsOrder } from '@hooks/useTrimsOrder';
import { ICarDetail } from 'src/redux/carDetails/carDetails.duck.interface';
import Spinner from '@components/Spinner';
import { getMonthlyPriceObjByType } from '@utils/Price.utils';
import { CarJourneyType, IFeature, IPriceV2Types } from 'src/services';
import CarDetailsService from 'src/services/carDetails/carDetails.service';
import PriceSuffix from '@components/PriceSuffix';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST, FeatureSwitchContext } from 'src/context/featureSwitchApp';
import { orderBy } from 'lodash';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import { useRouter } from 'next/router';
import routes from 'src/constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import GlobalDuck from 'src/redux/global/global.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { EMPLOYEE_PRICE_TYPE } from 'src/services/filters/filters';
import { EContextPage, findPromoByPageAndType } from '@utils/Promo.utils';
import MarketingSticker from '@components/MarketingSticker';
import { IPromo } from 'src/interfaces/IOfferItem';
import parser from 'bbcode-to-react';
import { STANDARD_FEATURES_FS_PREFIX } from 'src/constants/characteristics';
import MonthlyPriceLabel, { AprSizes } from '@components/MonthlyPriceLabel';
import { SC } from 'src/styles/theme';
import { isBrandAC, isMarketGB, ModalVersion } from 'src/constants/main';
import { getCalculateSummary } from 'src/services/calculateSummary/calculateSummary.service';
import {
    getUniqueEngines,
    getUniqueInteriors,
    isEngineAvailableInConfiguration,
    isInteriorAvailableInConfiguration,
} from '@utils/TrimCompare.utils';
import Select from '@components/Select';
import { getCategoriesArrowColor } from '@components/Modal/ModalComparators/shared/helpers';
import { ICarFeature } from '../../../../interfaces/Car';
import { IGlobalState } from 'src/redux/redux.interface';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import { CloseButton } from '@components/Modal/ModalComparators/shared/CloseButton';
import { TabButton } from '@components/Modal/ModalComparators/shared/TabButton';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import Button from "@components/Button";

export const KEY_ALL = 'all';
export const KEY_CATEGORY = 'category';
export const KEY_PROPERTIES = 'property';
export const KEY_TRIMS = 'trim';
export const KEY_ENGINES = 'engine';
export const KEY_COLORS = 'color';
export const KEY_INTERIORS = 'interior';
export const KEY_FEATURES = 'feature';
export const KEY_FEATURES_DESCRIPTION = 'featureDescription';
export const KEY_STRONG_POINTS = 'title';

const KEY_STICKY_AVAILABILITY = 'SECTION_TRIM_AVAILABILITY';
const KEY_STICKY_NAME = 'SECTION_TRIM_NAME';
const KEY_STICKY_PRICE = 'SECTION_TRIM_PRICE';
const KEY_STICKY_BUTTON = 'SECTION_TRIM_BUTTON';
const KEY_STICKY_CATEGORY = 'SECTION_TRIM_CATEGORY';

const EXIST_FEATURE_IN_CONFIGURATION = 'existInConfiguration';
const NO_EXIST_FEATURE_IN_CONFIGURATION = 'noExistInConfiguration';
const SELECTED = 'selected';
const SCROLL_ELEMENT_CLASS_NAME = 'trim-compare';

const AVG_STICKY_HEIGHT = 250;
const AVG_TITLE_HEIGHT = 25;
export const TRIM_MOBILE_WIDTH = 150;
export const TRIM_DESKTOP_WIDTH = 280;
export const TRIM_MOBILE_WIDTH_WITH_PADDING = 175;
export const TRIM_DESKTOP_WIDTH_WITH_PADDING = 305;
export const ELEMENT_PADDING = 30;

export const TESTING_PREFIX = 'trims-comparator-';
export interface ITrimCompareStyled extends SC {
    countOfTrims?: number;
    className?: string;
}

const ModalTrimCompareTemplate = ({ className }: ITrimCompareStyled) => {
    const { t } = useTranslations();
    const { formatMoney } = useMoneyFormatter();
    const router = useRouter();
    const dispatch = useDispatch();

    const [activeSection, setActiveSection] = useState(KEY_ENGINES);
    const [activeFeature, setActiveFeature] = useState(KEY_ALL);

    const configurableTrims = useSelector(TrimSelectorDuck.getAllConfigurableTrims);

    const [trims, setTrims] = useState(null);
    const [uniqueEngines, setUniqueEngines] = useState([]);
    const [uniqueInteriorColors, setUniqueInteriorColors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const { carJourney } = useGlobalDuck();

    const { orderAscByCataloguePrice } = useTrimsOrder(trims, IPriceV2Types.TOTAL_B2C_CASH);

    const isMobile = () => {
        const modal = document?.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];

        return (modal as HTMLElement)?.offsetWidth <= 480;
    };

    useEffect(() => {
        setIsLoading(true);

        CarDetailsService.getCarsDetailsWithAllOptions(router?.query?.model?.[0] as string).then((trims) => {
            setTrims(trims);
            setUniqueEngines(getUniqueEngines(trims));
            setUniqueInteriorColors(getUniqueInteriors(trims));
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        const element = document?.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];
        const trimsTitle = document?.getElementById(`TITLE_${KEY_TRIMS}`);
        const enginesTitle = document?.getElementById(`TITLE_${KEY_ENGINES}`);
        const keyFeatures = document?.getElementById(`TITLE_${KEY_STRONG_POINTS}`);
        const colorsTitle = document?.getElementById(`TITLE_${KEY_COLORS}`);
        const interiorsTitle = document?.getElementById(`TITLE_${KEY_INTERIORS}`);
        const featuresTitle = document?.getElementById(`TITLE_${KEY_FEATURES}`);
        const featuresDescription = document?.getElementById(KEY_FEATURES_DESCRIPTION);

        const categoriesSection = document?.getElementById(KEY_STICKY_CATEGORY);

        const trimsSectionTitleTxt = document?.getElementById(`TITLE_TXT_${KEY_TRIMS}`);

        const closeButton = document?.getElementById('closeButton');

        closeButton.style.left = `${element?.clientWidth - 40}px`;

        element?.addEventListener('scroll', () => {
            trimsSectionTitleTxt.style.display = element.scrollTop > AVG_TITLE_HEIGHT ? 'none' : 'block';

            const trimWidth = isMobile() ? TRIM_MOBILE_WIDTH_WITH_PADDING : TRIM_DESKTOP_WIDTH_WITH_PADDING;

            categoriesSection.style.width =
                trims?.length >= 3 || element.clientWidth < trimWidth * 2
                    ? `${(element as HTMLElement)?.clientWidth}px`
                    : '100%';

            if (element.clientWidth + element?.scrollLeft <= (trims?.length || 1) * trimWidth + ELEMENT_PADDING) {
                trimsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                enginesTitle.style.marginLeft = `${element?.scrollLeft}px`;
                colorsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                interiorsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                featuresTitle.style.marginLeft = `${element?.scrollLeft}px`;
                featuresDescription.style.marginLeft = `${element?.scrollLeft}px`;
                categoriesSection.style.marginLeft = `${element?.scrollLeft}px`;
                if (keyFeatures) keyFeatures.style.marginLeft = `${element?.scrollLeft}px`;
                closeButton.style.left = `${element?.clientWidth + element?.scrollLeft - 40}px`;
            }
        });
    }, [trims]);

    useEffect(() => {
        const trimsSection = document?.getElementById(`SECTION_${KEY_TRIMS}`);
        const propertiesSection = document?.getElementById(`SECTION_${KEY_PROPERTIES}`);
        const featureSection = document?.getElementById(`SECTION_${KEY_FEATURES}`);

        const trimWidth = isMobile() ? TRIM_MOBILE_WIDTH_WITH_PADDING : TRIM_DESKTOP_WIDTH_WITH_PADDING;

        if (trimsSection) trimsSection.style.width = `${(trims?.length || 1) * trimWidth}px`;
        if (propertiesSection) propertiesSection.style.width = `${(trims?.length || 1) * trimWidth}px`;
        if (featureSection) featureSection.style.width = `${(trims?.length || 1) * trimWidth}px`;
    }, [trims]);

    useEffect(() => {
        if (trims) {
            const element = document?.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];

            const trimWidth = isMobile() ? TRIM_MOBILE_WIDTH_WITH_PADDING : TRIM_DESKTOP_WIDTH_WITH_PADDING;

            const featuresSection = document?.getElementById(`SECTION_${KEY_STRONG_POINTS}`);
            const colorsSection = document?.getElementById(`SECTION_${KEY_COLORS}`);
            const interiorsSection = document?.getElementById(`SECTION_${KEY_INTERIORS}`);
            const optionsSection = document?.getElementById(`SECTION_${KEY_FEATURES}`);

            const stickyAvailability = document.getElementById(KEY_STICKY_AVAILABILITY);

            const stickyName = document.getElementById(KEY_STICKY_NAME);
            const stickyPrice = document.getElementById(KEY_STICKY_PRICE);
            const stickyButton = document.getElementById(KEY_STICKY_BUTTON);
            const stickyCategory = document.getElementById(KEY_STICKY_CATEGORY);

            element?.addEventListener('scroll', () => {
                stickyPrice.style.top = `${stickyName.offsetHeight + AVG_TITLE_HEIGHT}px`;
                stickyButton.style.top = `${stickyName.offsetHeight + stickyPrice.offsetHeight + AVG_TITLE_HEIGHT}px`;
                stickyCategory.style.top = `${
                    stickyName.offsetHeight + stickyPrice.offsetHeight + stickyButton.offsetHeight + AVG_TITLE_HEIGHT
                }px`;

                stickyPrice.style.display =
                    element?.scrollTop > stickyName.offsetHeight + AVG_TITLE_HEIGHT && element.clientWidth < 480
                        ? 'none'
                        : 'block';

                const stickyWidth = `${
                    (element as HTMLElement)?.clientWidth > (trims?.length || 1) * trimWidth + ELEMENT_PADDING
                        ? `${(element as HTMLElement)?.clientWidth}px`
                        : `${(trims?.length || 1) * trimWidth}px`
                }`;
                const stickyHeight =
                    stickyAvailability.offsetHeight +
                    stickyName.offsetHeight +
                    stickyPrice.offsetHeight +
                    stickyButton.offsetHeight +
                    stickyCategory.offsetHeight;

                stickyAvailability.style.width = stickyWidth;
                stickyName.style.width = stickyWidth;
                stickyPrice.style.width = stickyWidth;
                stickyPrice.style.width = stickyWidth;
                stickyButton.style.width = stickyWidth;

                if (element.scrollTop <= colorsSection.offsetTop) setActiveSection(KEY_ENGINES);

                if (element.scrollTop >= featuresSection?.offsetTop - stickyHeight - 20)
                    setActiveSection(KEY_STRONG_POINTS);

                if (element.scrollTop >= colorsSection.offsetTop - stickyHeight - 20) setActiveSection(KEY_COLORS);

                if (element.scrollTop >= interiorsSection.offsetTop - stickyHeight - 20)
                    setActiveSection(KEY_INTERIORS);

                if (element.scrollTop >= optionsSection.offsetTop - stickyHeight - 20) setActiveSection(KEY_FEATURES);
                if (element.scrollTop + element.clientHeight === element.scrollHeight) setActiveSection(KEY_FEATURES);
            });
        }
    }, [trims]);

    const parsedTrims = orderAscByCataloguePrice();

    const handleColorClick = async (carDetails: ICarDetail, selectedExteriorColorSlug: string) => {
        setIsLoading(true);

        const newTrim = await CarDetailsService.getCarDetailsWithAllOptions(
            carDetails.nameplateBodyStyleSlug,
            carDetails.specPackSlug,
            carDetails.engineGearboxFuelSlug,
            selectedExteriorColorSlug,
            carDetails.interiorColourSlug,
            []
        );

        setTrims(
            parsedTrims.map((trim) => {
                return trim.vehicle.nameplateBodyStyleSlug === carDetails.nameplateBodyStyleSlug &&
                    trim.vehicle.specPackSlug === carDetails.specPackSlug &&
                    trim.vehicle.engineGearboxFuelSlug === carDetails.engineGearboxFuelSlug &&
                    trim.vehicle.interiorColourSlug === carDetails.interiorColourSlug &&
                    trim.vehicle.exteriorColourSlug === carDetails.exteriorColourSlug
                    ? newTrim
                    : trim;
            })
        );
        setUniqueEngines(getUniqueEngines(parsedTrims));
        setUniqueInteriorColors(getUniqueInteriors(parsedTrims));

        setIsLoading(false);

        const elementColor = document.getElementById(`SECTION_${KEY_PROPERTIES}`);
        const elementScroll = document.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];

        const trimWidth = isMobile() ? TRIM_MOBILE_WIDTH_WITH_PADDING : TRIM_DESKTOP_WIDTH_WITH_PADDING;

        elementColor?.scrollIntoView({ behavior: 'smooth' });
        elementScroll.scrollLeft = trimWidth * Math.floor(elementScroll.scrollLeft / trimWidth);

        return;
    };

    const handleCategoryClick = (key: string) => {
        const modal = document?.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];

        const element = document.getElementById(`SECTION_${key}`);

        const stickyName = document.getElementById(KEY_STICKY_NAME);
        const stickyPrice = document.getElementById(KEY_STICKY_PRICE);
        const stickyButton = document.getElementById(KEY_STICKY_BUTTON);
        const stickyCategory = document.getElementById(KEY_STICKY_CATEGORY);

        modal?.scrollTo({
            top:
                element.offsetTop -
                stickyName.offsetHeight -
                stickyPrice.offsetHeight -
                stickyButton.offsetHeight -
                stickyCategory.offsetHeight -
                35,
            behavior: 'smooth',
        });

        return;
    };

    const getMainImageUrl = (vehicle: ICarDetail) => {
        const images = vehicle?.images?.filter((img: any) => img.type === 'Ext') ?? [{}];
        const mainImage = images?.splice(1, 1) ?? [{}];

        return mainImage[0]?.url;
    };

    const redirectToConfigurator = (vehicle: ICarDetail) => {
        dispatch(UIDuck.closeModal());

        const currentPathParams = extractParamsFromPathname(router.asPath);
        const newPathParams = {
            nameplateBodyStyleSlug: currentPathParams.nameplateBodyStyleSlug,
            speckPackSlug: vehicle?.specPackSlug,
            engineGearboxFuelSlug: vehicle?.engineGearboxFuelSlug,
            exteriorColourSlug: vehicle?.exteriorColourSlug,
            interiorColourSlug: vehicle?.interiorColourSlug,
        };

        const modelURL = buildParameterizedPathname(routes.CAR, carJourney, newPathParams);

        return router.push(modelURL);
    };

    const calculateSummaryData = (carDetails: ICarDetail) => {
        getCalculateSummary({
            carConfigId: carDetails.externalId,
        }).then((data) => {
            dispatch(
                UIDuck.openModal({
                    data: {
                        financialData: data,
                    },
                    callbacks: {
                        openPreviousModalType: UIDuck.MODAL_TYPES.TRIM_COMPARE,
                    },
                    modalType: UIDuck.MODAL_TYPES.FINANCIAL_SUMMARY_CONFIGURATION,
                    modalVersion: ModalVersion.v2,
                })
            );
        });
    };

    const excludedCategoriesStr =
        useContext(FeatureSwitchContext)?.FEATURE_SWITCH_EXCLUDED_CHARACTERISTICS_CATEGORIES ?? '';
    const isPromoEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_ENABLED);
    const hasAnyTrimOffers =
        trims?.filter((trim: { vehicle: ICarDetail }) =>
            findPromoByPageAndType(trim?.vehicle?.offers, EContextPage.trim)
        ).length > 0;
    const hasAnyTrimLabel =
        trims?.filter((trim: { vehicle: ICarDetail }) => trim?.vehicle?.trimContent?.title)?.length > 0;
    const hasAnyTrimDescription =
        trims?.filter((trim: { vehicle: ICarDetail }) => trim?.vehicle?.trimContent?.description)?.length > 0;
    const hasAnyTrimMainFeatures =
        configurableTrims?.filter(({ extraFields }) => extraFields?.seriesEquipment?.length)?.length > 0;

    const uniqueFeaturesKeys = [] as string[];

    const getCategoryLabelByKey = (key: string) => {
        switch (key) {
            case KEY_ENGINES:
                return t('modal.features.keys.engine');
            case KEY_STRONG_POINTS:
                return t('modal.features.keys.title');
            case KEY_COLORS:
                return t('modal.features.keys.color');
            case KEY_INTERIORS:
                return t('modal.features.keys.interior');
            case KEY_FEATURES:
                return t('modal.features.keys.feature');
        }
    };

    const renderCategories = () => (
        <>
            {trims?.length >= 3 && (
                <div data-testid={`${TESTING_PREFIX}filters`} className="desktop">
                    {[
                        KEY_ENGINES,
                        ...(hasAnyTrimMainFeatures ? [KEY_STRONG_POINTS] : []),
                        KEY_COLORS,
                        KEY_INTERIORS,
                        KEY_FEATURES,
                    ].map((key, index) => (
                        <TabButton
                            key={index}
                            className={activeSection === key ? SELECTED : ''}
                            onClick={() => handleCategoryClick(key)}
                        >
                            {t(`modal.features.keys.${key}`)}
                            {isBrandAC && <div className="border" />}
                        </TabButton>
                    ))}
                </div>
            )}
            <div
                data-testid={`${TESTING_PREFIX}filters`}
                className={`mobile ${trims?.length >= 3 ? 'hideOnDesktop' : ''}`}
            >
                <Select
                    value={{ value: activeSection, label: getCategoryLabelByKey(activeSection) }}
                    onChange={(selectedOption: { value: string; label: string }) =>
                        handleCategoryClick(selectedOption.value)
                    }
                    options={[
                        KEY_ENGINES,
                        ...(hasAnyTrimMainFeatures ? [KEY_STRONG_POINTS] : []),
                        KEY_COLORS,
                        KEY_INTERIORS,
                        KEY_FEATURES,
                    ].map((key) => ({
                        value: key,
                        label: getCategoryLabelByKey(key),
                    }))}
                    fullWidth
                    withBorder
                    arrowColor={getCategoriesArrowColor()}
                    isSearchable={false}
                />
            </div>
        </>
    );

    return (
        <div className={`${className} test`} id="trims" data-testid={`${TESTING_PREFIX}modal`}>
            <div className="trims stickyTrimAvailability" id={KEY_STICKY_AVAILABILITY}>
                <CloseButton
                    className="closeButton"
                    id="closeButton"
                    onClick={() => dispatch(UIDuck.closeModal())}
                    data-testid="TESTING_CLOSE_MODAL"
                >
                    &#10005;
                </CloseButton>
                <div className="title" id={`TITLE_${KEY_TRIMS}`}>
                    <span className="txt" id={`TITLE_TXT_${KEY_TRIMS}`}>
                        {t('trim.comparator.title')}
                    </span>
                    <div className="availability" id={`TITLE_AVAILABILITY_${KEY_TRIMS}`}>
                        <span className="item">
                            <div className="icon available" />
                            {t('trim.comparator.availability.available')}
                        </span>
                        <span className="item">
                            <div className="icon" />
                            {t('trim.comparator.availability.noAvailable')}
                        </span>
                        <span className="item">
                            <div className="icon optional" />
                            {t('trim.comparator.availability.optional')}
                        </span>
                    </div>
                </div>
            </div>
            <div className="trims" id={`SECTION_${KEY_TRIMS}`}>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="sections">
                        {parsedTrims?.map((trim, trimKey: number) => {
                            const hasTrimOffers = findPromoByPageAndType(trim?.vehicle?.offers, EContextPage.trim);

                            return (
                                <div key={`trim-${trimKey}`} className="trim">
                                    {isPromoEnabled && hasTrimOffers && (
                                        <div className="trimPromo">
                                            <MarketingSticker
                                                data={
                                                    findPromoByPageAndType(
                                                        trim?.vehicle?.offers,
                                                        EContextPage.trim
                                                    ) as unknown as Array<IPromo>
                                                }
                                                page={EContextPage.trim}
                                            />
                                        </div>
                                    )}
                                    {isPromoEnabled && !hasTrimOffers && hasAnyTrimOffers && (
                                        <div className="trimPromoSpace" />
                                    )}
                                    <img className="trimImage" src={getMainImageUrl(trim?.vehicle)} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="trims stickyTrimName" id={KEY_STICKY_NAME}>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="sections">
                        {parsedTrims?.map((trim, trimKey: number) => (
                            <div key={`trim-${trimKey}`} className="trim">
                                <span className="trimName">{trim?.vehicle?.specPack?.title}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {(hasAnyTrimLabel || hasAnyTrimDescription) && (
                <div className="trims">
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="sections">
                            {parsedTrims?.map((trim, trimKey: number) => (
                                <div key={`trim-${trimKey}`} className="trim">
                                    <span className="trimLabel">
                                        {trim?.vehicle?.trimContent?.title
                                            ? parser.toReact(trim.vehicle.trimContent.title)
                                            : null}
                                    </span>
                                    <span className="trimDescription">
                                        {trim?.vehicle?.trimContent?.description
                                            ? parser.toReact(trim.vehicle.trimContent.description)
                                            : null}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            <div className="trims stickyTrimPrice" id={KEY_STICKY_PRICE}>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="sections">
                        {parsedTrims?.map((trim, trimKey: number) => {
                            const monthlyPrice = getMonthlyPriceObjByType(trim?.vehicle?.prices, EMPLOYEE_PRICE_TYPE);

                            return (
                                <div key={`trim-${trimKey}`} className="trim">
                                    <span className="trimPrice">
                                        {t('nameplate.priceFrom')} {formatMoney(trim.parsedPrice, true)}{' '}
                                        {monthlyPrice && (
                                            <span className="monthlyPrice">
                                                <span className="or">{t('configurator.optionPrice.suffix')}</span>{' '}
                                                {formatMoney(monthlyPrice.amount, true)}
                                                <PriceSuffix isMonthlyPrice hideAsterisk />
                                            </span>
                                        )}
                                        {monthlyPrice && isMarketGB && (
                                            <MonthlyPriceLabel
                                                withPrimaryColor={isMarketGB}
                                                aprValue={monthlyPrice.apr}
                                                size={AprSizes.SMALL}
                                                calculateSummaryData={() => calculateSummaryData(trim.vehicle)}
                                            />
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="trims stickyTrimButton" id={KEY_STICKY_BUTTON}>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="sections">
                        {parsedTrims?.map((trim, trimKey: number) => (
                            <div key={`trim-${trimKey}`} className="trim">
                                <Button onClick={() => redirectToConfigurator(trim?.vehicle)} primary marginBottom={15}>
                                    {t('trim.comparator.select')}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="categories" id={KEY_STICKY_CATEGORY}>
                {renderCategories()}
            </div>
            <div
                className="properties withoutBorder"
                id={`SECTION_${KEY_PROPERTIES}`}
                data-testid={`${TESTING_PREFIX}properties`}
            >
                <div className="enginesAvailable" id={`SECTION_${KEY_ENGINES}`}>
                    <span className="label" id={`TITLE_${KEY_ENGINES}`}>
                        {t('modal.features.keys.engine')}
                    </span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="sections">
                            {parsedTrims?.map(
                                (
                                    trim: {
                                        vehicle: ICarDetail;
                                        features: { engines: IFeature[]; interiorColors: IFeature[] };
                                    },
                                    trimKey: number
                                ) => (
                                    <div key={`engines-${trimKey}`} className="engines">
                                        {uniqueEngines.map((motorization: IFeature, engineKey: number) => {
                                            const getPower = () =>
                                                motorization?.characteristics?.find(
                                                    (char: { key: string }) => char.key === 'maximumPowerKW'
                                                )?.value;

                                            return (
                                                <div
                                                    key={`${trimKey}-engine-${engineKey}`}
                                                    className={`engine ${
                                                        isEngineAvailableInConfiguration(trim, motorization)
                                                            ? EXIST_FEATURE_IN_CONFIGURATION
                                                            : NO_EXIST_FEATURE_IN_CONFIGURATION
                                                    }`}
                                                >
                                                    <div className="engineDetails">
                                                        <span className="details">
                                                            {`${motorization.fuel}, ${motorization.gearboxTitle}${
                                                                motorization?.characteristics?.length >= 1
                                                                    ? `, ${getPower()} ${t(
                                                                          'configurator.info.power.label'
                                                                      )}`
                                                                    : ''
                                                            }`}
                                                        </span>
                                                        <span className="name hideOnMobile">{motorization.title}</span>
                                                    </div>
                                                    <div
                                                        className={`iconAvailability ${
                                                            isEngineAvailableInConfiguration(trim, motorization)
                                                                ? 'available'
                                                                : 'noAvailable'
                                                        }`}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
                {hasAnyTrimMainFeatures && (
                    <div
                        className="keyFeatures"
                        id={`SECTION_${KEY_STRONG_POINTS}`}
                        data-testid={`${TESTING_PREFIX}section${KEY_STRONG_POINTS}`}
                    >
                        <span className="label" id={`TITLE_${KEY_STRONG_POINTS}`}>
                            {t('modal.features.point.title')}
                        </span>
                        {isLoading ? (
                            <Spinner border={2} size={25} color="black" />
                        ) : (
                            <div
                                className="sections"
                                data-testid={`${TESTING_PREFIX}items-section${KEY_STRONG_POINTS}`}
                            >
                                {configurableTrims?.map(({ extraFields }, trimIndex) => (
                                    <div key={`keyFeatures-${trimIndex}`} className="keyFeaturesConfig">
                                        {extraFields?.seriesEquipment.map(
                                            (seriesEquipment: ICarFeature, equipmentIndex) => {
                                                const data = seriesEquipment.data;

                                                return (
                                                    <div
                                                        key={`keyFeatures-${trimIndex}-${equipmentIndex}`}
                                                        className="config"
                                                    >
                                                        {Array.isArray(data)
                                                            ? data.map((equipment) => (
                                                                  <span className="configDetails" key={equipment.label}>
                                                                      {equipment.label}
                                                                  </span>
                                                              ))
                                                            : Object.keys(data).map((key) => (
                                                                  <span className="configDetails" key={data[key].label}>
                                                                      {data[key].label}
                                                                  </span>
                                                              ))}
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className="colors" id={`SECTION_${KEY_COLORS}`}>
                    <span className="label" id={`TITLE_${KEY_COLORS}`}>
                        {t('modal.features.color.title')}
                    </span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="sections">
                            {parsedTrims?.map((trim, trimKey: number) => (
                                <div key={`exteriorColors-${trimKey}`} className="configurations">
                                    <img className="image" src={getMainImageUrl(trim?.vehicle)} />
                                    <div className="colorButtons">
                                        {trim?.features.exteriorColors?.map(
                                            (exteriorColor, exteriorColorKey: number) => (
                                                <button
                                                    key={`${trimKey}-exteriorColor-${exteriorColorKey}`}
                                                    className={`colorButton enabled ${
                                                        trim?.vehicle?.exteriorColourSlug ===
                                                        exteriorColor.exteriorColourSlug
                                                            ? SELECTED
                                                            : ''
                                                    }`}
                                                    role="button"
                                                    style={{
                                                        backgroundImage: `url('${exteriorColor.images[0]?.url}')`,
                                                    }}
                                                    onClick={() =>
                                                        handleColorClick(
                                                            trim?.vehicle,
                                                            exteriorColor.exteriorColourSlug
                                                        )
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="interiorsAvailable" id={`SECTION_${KEY_INTERIORS}`}>
                    <span className="label" id={`TITLE_${KEY_INTERIORS}`}>
                        {t('modal.features.keys.interior')}
                    </span>
                    {isLoading ? (
                        <Spinner border={2} size={25} color="black" />
                    ) : (
                        <div className="sections">
                            {parsedTrims?.map(
                                (
                                    trim: {
                                        vehicle: ICarDetail;
                                        features: { engines: IFeature[]; interiorColors: IFeature[] };
                                    },
                                    trimKey: number
                                ) => (
                                    <div key={`interiorColors-${trimKey}`} className="interiors">
                                        {uniqueInteriorColors.map(
                                            (interiorColor: IFeature, interiorColorKey: number) => {
                                                return (
                                                    <div
                                                        key={`${trimKey}-interiorColor-${interiorColorKey}`}
                                                        className={`interior ${
                                                            isInteriorAvailableInConfiguration(trim, interiorColor)
                                                                ? EXIST_FEATURE_IN_CONFIGURATION
                                                                : NO_EXIST_FEATURE_IN_CONFIGURATION
                                                        }`}
                                                    >
                                                        <span className="name">{interiorColor.title}</span>
                                                        <div
                                                            className={`iconAvailability ${
                                                                isInteriorAvailableInConfiguration(trim, interiorColor)
                                                                    ? 'available'
                                                                    : 'noAvailable'
                                                            }`}
                                                        />
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="options withoutBorder">
                <span className="label" id={`TITLE_${KEY_FEATURES}`}>
                    {t('modal.features.feature.title')}
                </span>
                <p className="description" id={KEY_FEATURES_DESCRIPTION}>
                    {t('modal.features.feature.description')}
                </p>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="filter">
                        <button
                            className={activeFeature === KEY_ALL ? SELECTED : ''}
                            onClick={() => setActiveFeature(KEY_ALL)}
                        >
                            {t('modal.features.keys.all')}
                        </button>
                        {parsedTrims?.map((trim, trimKey: number) =>
                            orderBy(trim?.vehicle?.standardFeatures, ['category'], ['asc'])
                                ?.filter(
                                    (feature: { key: string }) =>
                                        !excludedCategoriesStr?.includes(
                                            `${STANDARD_FEATURES_FS_PREFIX}:${feature.key}`
                                        )
                                )
                                .map((feature: { key: string; category: string }, featureKey: number) => {
                                    if (!uniqueFeaturesKeys.includes(feature.category)) {
                                        uniqueFeaturesKeys.push(feature.category);

                                        return (
                                            <button
                                                key={`features-${trimKey}-${featureKey}`}
                                                className={activeFeature === feature.category ? SELECTED : ''}
                                                onClick={() => setActiveFeature(feature.category)}
                                            >
                                                {feature.category}
                                            </button>
                                        );
                                    }
                                })
                        )}
                    </div>
                )}
            </div>
            <div id={`SECTION_${KEY_FEATURES}`}>
                {uniqueFeaturesKeys.map((uniqueFeatureCategory: string, renderedFeatureKey: number) => {
                    if (activeFeature === KEY_ALL || activeFeature === uniqueFeatureCategory) {
                        return (
                            <div key={`feature-${renderedFeatureKey}`} className="featuresAvailable">
                                <span className="label">{uniqueFeatureCategory}</span>
                                <div className="sections">
                                    {parsedTrims?.map((trim, trimKey: number) => (
                                        <div key={`${trimKey}-feature`} className="features">
                                            {trim?.vehicle?.standardFeatures
                                                ?.filter(
                                                    (feature: { key: string }) =>
                                                        !excludedCategoriesStr?.includes(
                                                            `${STANDARD_FEATURES_FS_PREFIX}:${feature.key}`
                                                        )
                                                )
                                                ?.find(
                                                    (feature: { category: string }) =>
                                                        feature.category === uniqueFeatureCategory
                                                )
                                                ?.data?.map((configuration: { label: string }, key: number) => (
                                                    <div
                                                        key={`${trimKey}-feature-${configuration.label}-${key}`}
                                                        className="feature"
                                                    >
                                                        <span className="name">{configuration.label}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};

export default ModalTrimCompareTemplate;
